import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { SUBJECTS, ALL_FLASHCARDS } from '@/data/flashcards';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, RotateCcw, Check, X, Eye, Shuffle, Trophy, Target, BookOpen } from 'lucide-react';

const FlashcardStudy = ({ subjectId, onBack }) => {
  const { updateUserStats, getSubjectProgress } = useAuth();
  const subject = SUBJECTS.find(s => s.id === subjectId);
  const allCards = ALL_FLASHCARDS[subjectId] || [];
  
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [sessionStats, setSessionStats] = useState({ correct: 0, incorrect: 0 });
  const [isFinished, setIsFinished] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);

  // Shuffle cards on mount
  useEffect(() => {
    const shuffled = [...allCards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  const currentCard = cards[currentIndex];
  const progress = getSubjectProgress(subjectId);

  const handleFlip = useCallback(() => {
    setIsFlipped(!isFlipped);
    setShowAnswer(!showAnswer);
  }, [isFlipped, showAnswer]);

  const handleAnswer = useCallback((isCorrect) => {
    if (!currentCard) return;

    // Update stats
    updateUserStats(subjectId, currentCard.id, isCorrect);
    
    setSessionStats(prev => ({
      correct: isCorrect ? prev.correct + 1 : prev.correct,
      incorrect: !isCorrect ? prev.incorrect + 1 : prev.incorrect
    }));

    // Move to next card or finish
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
      setShowAnswer(false);
    } else {
      setIsFinished(true);
    }
  }, [currentCard, currentIndex, cards.length, subjectId, updateUserStats]);

  const handleRestart = () => {
    const shuffled = [...allCards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
    setShowAnswer(false);
    setSessionStats({ correct: 0, incorrect: 0 });
    setIsFinished(false);
  };

  // Results Screen
  if (isFinished) {
    const totalAnswered = sessionStats.correct + sessionStats.incorrect;
    const percentage = Math.round((sessionStats.correct / totalAnswered) * 100);

    return (
      <div className="min-h-screen bg-background flex flex-col">
        {/* Header */}
        <div className="px-4 pt-6 pb-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="font-semibold text-foreground">Ergebnis</h2>
            <p className="text-sm text-muted-foreground">{subject?.name}</p>
          </div>
        </div>

        {/* Results Card */}
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-6 animate-scale-in">
            <Trophy className="w-12 h-12 text-primary" />
          </div>

          <h2 className="text-3xl font-display font-bold text-foreground mb-2">
            {percentage}% Richtig!
          </h2>
          <p className="text-muted-foreground mb-8">
            {sessionStats.correct} von {totalAnswered} Karten
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 w-full max-w-xs mb-8">
            <Card className="bg-success/10 border-success/20">
              <CardContent className="p-4 text-center">
                <Check className="w-6 h-6 text-success mx-auto mb-2" />
                <p className="text-2xl font-bold text-success">{sessionStats.correct}</p>
                <p className="text-xs text-success/80">Gewusst</p>
              </CardContent>
            </Card>
            <Card className="bg-destructive/10 border-destructive/20">
              <CardContent className="p-4 text-center">
                <X className="w-6 h-6 text-destructive mx-auto mb-2" />
                <p className="text-2xl font-bold text-destructive">{sessionStats.incorrect}</p>
                <p className="text-xs text-destructive/80">Nicht gewusst</p>
              </CardContent>
            </Card>
          </div>

          {/* Actions */}
          <div className="w-full max-w-xs space-y-3">
            <Button onClick={handleRestart} className="w-full" size="lg">
              <RotateCcw className="w-4 h-4 mr-2" />
              Nochmal üben
            </Button>
            <Button variant="outline" onClick={onBack} className="w-full" size="lg">
              Zurück zur Übersicht
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentCard) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shuffle className="w-12 h-12 text-muted-foreground mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Karten werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                {subject?.icon} {subject?.name}
              </h2>
              <p className="text-sm text-muted-foreground">
                Karte {currentIndex + 1} von {cards.length}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-success text-sm font-medium">{sessionStats.correct}</span>
            <span className="text-muted-foreground">/</span>
            <span className="text-destructive text-sm font-medium">{sessionStats.incorrect}</span>
          </div>
        </div>

        {/* Progress Bar */}
        <Progress 
          value={((currentIndex + 1) / cards.length) * 100} 
          className="h-1.5 bg-muted"
        />
      </div>

      {/* Flashcard */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 pb-4">
        <div 
          className="w-full max-w-md perspective-1000 cursor-pointer"
          onClick={handleFlip}
        >
          <div 
            className={`relative w-full min-h-[320px] transition-transform duration-500 transform-style-3d ${isFlipped ? 'rotate-y-180' : ''}`}
            style={{ 
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Front of Card (Question) */}
            <Card 
              className="absolute inset-0 bg-card border-border shadow-lg"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <CardContent className="p-6 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-medium text-primary px-2 py-1 rounded-full bg-primary/10">
                    Frage
                  </span>
                  <span className="text-xs text-muted-foreground">
                    #{currentCard.id}
                  </span>
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-lg text-foreground text-center leading-relaxed">
                    {currentCard.question}
                  </p>
                </div>
                <div className="text-center mt-4">
                  <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Eye className="w-3 h-3" />
                    Tippe zum Umdrehen
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Back of Card (Answer) */}
            <Card 
              className="absolute inset-0 bg-card border-primary/30 shadow-lg"
              style={{ 
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }}
            >
              <CardContent className="p-6 h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-medium text-success px-2 py-1 rounded-full bg-success/10">
                    Antwort
                  </span>
                  <span className="text-xs text-muted-foreground">
                    #{currentCard.id}
                  </span>
                </div>
                <div className="flex-1 flex items-center justify-center overflow-y-auto">
                  <p className="text-base text-foreground text-center leading-relaxed">
                    {currentCard.answer}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-4 pb-8 safe-area-bottom">
        {!showAnswer ? (
          <Button 
            onClick={handleFlip} 
            className="w-full" 
            size="lg"
          >
            <Eye className="w-4 h-4 mr-2" />
            Antwort anzeigen
          </Button>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="outline"
              onClick={() => handleAnswer(false)} 
              className="h-14 border-destructive/50 text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <X className="w-5 h-5 mr-2" />
              Nicht gewusst
            </Button>
            <Button 
              onClick={() => handleAnswer(true)} 
              className="h-14 bg-success hover:bg-success/90 text-success-foreground"
            >
              <Check className="w-5 h-5 mr-2" />
              Gewusst
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashcardStudy;